import { field, parseEther, readContract, writeContractAndWait } from "../viem";
import { approveToken, executeBatchRequest, getTokenAllowance } from ".";
import MafiaFamily from "../../abis/MafiaFamily.json";
import config from "../../config/config";
import { FamilyInfo } from "../../types/Contract/Family/FamilyInfo";
import { PlayerInfo } from "../../types/Contract/Family/PlayerInfo";

const address = config.mafiaFamilyAddress;
const abi = MafiaFamily;

export const hasFamily = async (account: string) => {
  const result: boolean = await readContract({
    address,
    abi,
    functionName: "hasFamily",
    args: [account],
  });

  return result;
};

export const isFamilyMember = async (account: string, familyId: number) => {
  const result: boolean = await readContract({
    address,
    abi,
    functionName: "isFamilyMember",
    args: [account, familyId],
  });

  return result;
};

export const getFamilyId = async (account: string) => {
  const result: PlayerInfo = await readContract({
    address,
    abi,
    functionName: "getPlayerInfo",
    args: [account],
  });

  return Number(field(result, "familyId", 0));
};

export const getFamilyInfo = async (familyId: number) => {
  const result: FamilyInfo = await readContract({
    address,
    abi,
    functionName: "getFamily",
    args: [familyId],
  });

  return result;
};

export const getFamilyIdsCount = async () => {
  const result = await readContract<string>({
    address,
    abi,
    functionName: "familyIds",
  });

  return Number(result);
};

const normalizeFamilyInfo = (result: FamilyInfo): FamilyInfo => ({
  familyId: Number(result.familyId),
  leaders: Array.isArray(result.leaders) ? result.leaders : [],
  successor: result.successor,
  leaveFee: Number(result.leaveFee),
  memberCount: Number(result.memberCount),
  isDead: Boolean(result.isDead),
});

/** Enumerate all on-chain families and return those marked dead. */
export const getDeadFamilies = async (): Promise<FamilyInfo[]> => {
  const count = await getFamilyIdsCount();
  if (count < 1) return [];

  const batchSize = 50;
  const deadFamilies: FamilyInfo[] = [];

  for (let start = 1; start <= count; start += batchSize) {
    const end = Math.min(start + batchSize - 1, count);
    const requests: Array<() => Promise<FamilyInfo | null>> = [];

    for (let familyId = start; familyId <= end; familyId += 1) {
      requests.push(async () => {
        try {
          return await readContract<FamilyInfo>({
            address,
            abi,
            functionName: "getFamily",
            args: [familyId],
          });
        } catch {
          return null;
        }
      });
    }

    const results = await executeBatchRequest(requests);
    for (const result of results) {
      if (!result) continue;

      const family = normalizeFamilyInfo(result);
      if (family.isDead && family.familyId > 0) {
        deadFamilies.push(family);
      }
    }
  }

  return deadFamilies;
};

export const getFamilyInvitationLogs = async (familyId: number, id: number) => {
  const result = await readContract({
    address,
    abi,
    functionName: "familyInvitationLogs",
    args: [familyId, id],
  });

  return result;
};

export const getMinFamilyLeaveFee = async () => {
  const result: number = await readContract({
    address,
    abi,
    functionName: "minLeaveFee",
  });

  return result;
};

export const getMaxFamilyLeaveFee = async () => {
  const result: number = await readContract({
    address,
    abi,
    functionName: "maxLeaveFee",
  });

  return result;
};

export const createFamily = async (account: string) => {
  const data = await writeContractAndWait({
    address,
    abi,
    functionName: "createFamily",
    account,
  });

  return data;
};

export const approveFamilyContract = async (
  account: string,
  approveAmount: number
) => {
  const tokenAddress = config.mafiaAddress;
  const contractAddress = config.mafiaFamilyAddress;

  const allowance = await getTokenAllowance(
    tokenAddress,
    account,
    contractAddress
  );

  if (Number(allowance) < approveAmount) {
    return await approveToken(
      tokenAddress,
      approveAmount,
      contractAddress,
      account
    );
  }
};

export const inviteMember = async (
  account: string,
  familyId: number,
  invitee: string
) => {
  const data = await writeContractAndWait({
    address,
    abi,
    functionName: "inviteMember",
    args: [familyId, invitee],
    account,
  });

  return data;
};

export const acceptInvitation = async (
  account: string,
  invitationId: number
) => {
  const data = await writeContractAndWait({
    address,
    abi,
    functionName: "acceptInvitation",
    args: [invitationId],
    account,
  });

  return data;
};

export const rejectInvitation = async (
  account: string,
  invitationId: number
) => {
  const data = await writeContractAndWait({
    address,
    abi,
    functionName: "rejectInvitation",
    args: [invitationId],
    account,
  });

  return data;
};

export const cancelInvitation = async (
  account: string,
  invitationId: number
) => {
  const data = await writeContractAndWait({
    address,
    abi,
    functionName: "cancelInvitation",
    args: [invitationId],
    account,
  });

  return data;
};

export const updateLeader = async (
  account: string,
  familyId: number,
  memberAddress: string,
  leaderIndex: number
) => {
  const data = await writeContractAndWait({
    address,
    abi,
    functionName: "updateFamilyLeader",
    args: [familyId, memberAddress, leaderIndex],
    account,
  });

  return data;
};

export const setSuccessor = async (
  account: string,
  familyId: number,
  successor: string
) => {
  const data = await writeContractAndWait({
    address,
    abi,
    functionName: "setSuccessor",
    args: [familyId, successor],
    account,
  });

  return data;
};

export const leaveFamily = async (account: string, familyId: number) => {
  const data = await writeContractAndWait({
    address,
    abi,
    functionName: "leaveFamily",
    args: [familyId],
    account,
  });

  return data;
};

export const kickMember = async (
  account: string,
  familyId: number,
  memberAddress: string
) => {
  const data = await writeContractAndWait({
    address,
    abi,
    functionName: "kickMember",
    args: [familyId, memberAddress],
    account,
  });

  return data;
};

export const updateFamilyLeaveFee = async (
  account: string,
  familyId: number,
  value: number
) => {
  const data = await writeContractAndWait({
    address,
    abi,
    functionName: "updateFamilyLeaveFee",
    args: [familyId, parseEther(value.toString())],
    account,
  });

  return data;
};
